import { useState } from 'react';
import {
  Box,
  Card,
  CardContent,
  TextField,
  Button,
  Typography,
  Alert,
  Link,
  CircularProgress,
} from '@mui/material';
import { useAuth } from '../../contexts/AuthContext';

interface ConfirmationFormProps {
  email: string;
  onConfirmationSuccess: () => void;
  onBackToSignUp: () => void;
}

export function ConfirmationForm({ email, onConfirmationSuccess, onBackToSignUp }: ConfirmationFormProps) {
  const { confirmSignUp, resendConfirmationCode, error, loading, clearError } = useAuth();
  const [confirmationCode, setConfirmationCode] = useState('');
  const [isResending, setIsResending] = useState(false);
  const [resendMessage, setResendMessage] = useState<string | null>(null);

  const handleSubmit = async (e: React.FormEvent) => {
    e.preventDefault();
    
    if (!confirmationCode.trim()) {
      return;
    }

    try {
      await confirmSignUp(email, confirmationCode);
      onConfirmationSuccess();
    } catch (err) {
      // Error is handled by the auth context
    }
  };

  const handleResendCode = async () => {
    try {
      setIsResending(true);
      setResendMessage(null);
      clearError();
      
      await resendConfirmationCode(email);
      setResendMessage('Confirmation code sent! Check your email.');
    } catch (err) {
      // Error is handled by the auth context
    } finally {
      setIsResending(false);
    }
  };

  const handleInputChange = (e: React.ChangeEvent<HTMLInputElement>) => {
    clearError(); // Clear errors when user starts typing
    setResendMessage(null);
    setConfirmationCode(e.target.value);
  };

  return (
    <Box
      display="flex"
      justifyContent="center"
      alignItems="center"
      minHeight="100vh"
      bgcolor="background.default"
      p={2}
    >
      <Card sx={{ maxWidth: 400, width: '100%' }}>
        <CardContent sx={{ p: 4 }}>
          {/* TimeLynxx Branding */}
          <Box textAlign="center" mb={4}>
            <Typography
              variant="h4"
              component="h1"
              sx={{
                fontWeight: 'bold',
                fontStyle: 'italic',
                color: 'primary.main',
                mb: 1,
              }}
            >
              Time
              <Box
                component="span"
                sx={{ color: 'secondary.main' }}
              >
                Lynxx
              </Box>
            </Typography>
            <Typography variant="body2" color="text.secondary">
              Track Your Time. Build Your Future.
            </Typography>
          </Box>

          <Typography variant="h5" component="h2" textAlign="center" mb={2}>
            Confirm Your Email
          </Typography>

          <Typography variant="body2" color="text.secondary" textAlign="center" mb={3}>
            We sent a confirmation code to:
            <br />
            <strong>{email}</strong>
          </Typography>

          {error && (
            <Alert severity="error" sx={{ mb: 2 }}>
              {error}
            </Alert>
          )}

          {resendMessage && (
            <Alert severity="success" sx={{ mb: 2 }}>
              {resendMessage}
            </Alert>
          )}

          <Box component="form" onSubmit={handleSubmit}>
            <TextField
              fullWidth
              label="Confirmation Code"
              value={confirmationCode}
              onChange={handleInputChange}
              margin="normal"
              required
              disabled={loading}
              autoComplete="one-time-code"
              autoFocus
              placeholder="123456"
              inputProps={{
                maxLength: 6,
                style: { textAlign: 'center', fontSize: '1.2em', letterSpacing: '0.5em' }
              }}
            />

            <Button
              type="submit"
              fullWidth
              variant="contained"
              disabled={loading || !confirmationCode.trim()}
              sx={{ mt: 3, mb: 2, py: 1.5 }}
            >
              {loading ? (
                <>
                  <CircularProgress size={20} sx={{ mr: 1 }} />
                  Confirming...
                </>
              ) : (
                'Confirm Email'
              )}
            </Button>

            <Box textAlign="center" mb={2}>
              <Button
                onClick={handleResendCode}
                disabled={isResending || loading}
                variant="text"
                size="small"
              >
                {isResending ? (
                  <>
                    <CircularProgress size={16} sx={{ mr: 1 }} />
                    Sending...
                  </>
                ) : (
                  'Resend Code'
                )}
              </Button>
            </Box>

            <Box textAlign="center">
              <Typography variant="body2" color="text.secondary">
                Wrong email?{' '}
                <Link
                  component="button"
                  type="button"
                  onClick={onBackToSignUp}
                  disabled={loading}
                  sx={{ textDecoration: 'none' }}
                >
                  Back to Sign Up
                </Link>
              </Typography>
            </Box>
          </Box>
        </CardContent>
      </Card>
    </Box>
  );
}